import { locanestApiService, InviteUserRequest, InviteUserResponse, UserSettings } from '@/lib/api-functions/locanest-api-service';
import { apiService } from '@/lib/api-functions/api-service';
import { UserProfile } from '@/lib/stores/storeTypes';

export interface UserGameAccess {
  gameId: string;
  gameName: string;
  hasAccess: boolean;
}

export interface ExtendedUser extends UserProfile {
  gameAccess: UserGameAccess[];
  lastLogin?: string;
  role: string; // Changed to string to support custom roles
  company_id: string;
  pendingChanges?: {
    role?: string;
    gameAccess?: UserGameAccess[];
  };
}

export interface CreateUserOptions {
  email: string;
  settings: UserSettings;
  role: string; // Changed to string to support custom roles
  companyId: string;
  gameIds?: string[];
  gameAccess?: UserGameAccess[];
}

export interface UpdateUserOptions {
  userId: string;
  role?: string;
  gameAccess?: UserGameAccess[];
  companyId: string;
}

export interface UserValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface UserManagementOperationResult {
  success: boolean;
  message: string;
  error?: string;
  data?: any;
}

export interface DefaultUserSettings {
  theme: string;
  language: string;
  timezone: string;
  scriptType?: string; // Script/writing system preference
  showEmail: boolean;
  defaultGame: string;
  defaultCompany: string;
  defaultProjectMode: string; // "last-used" or "specific"
  showLastLogin: boolean;
  projectUpdates: boolean;
  marketingEmails: boolean;
  twoFactorEnabled: boolean;
  profileVisibility: string;
  pushNotifications: boolean;
  emailNotifications: boolean;
}

/**
 * User Management Utility Functions
 * Centralizes all user management operations for better maintainability and reusability
 */
export class UserManagementUtils {
  /**
   * Gets the default settings for new user profiles
   * These settings match the backend configuration
   */
  static getDefaultUserSettings(): DefaultUserSettings {
    return {
      theme: "light",
      language: "en",
      timezone: "Europe/Brussels",
      scriptType: "latin",
      showEmail: false,
      defaultGame: "",
      defaultCompany: "",
      defaultProjectMode: "last-used",
      showLastLogin: true,
      projectUpdates: true,
      marketingEmails: true,
      twoFactorEnabled: false,
      profileVisibility: "public",
      pushNotifications: true,
      emailNotifications: true
    };
  }

  /**
   * Validates email format
   */
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Validates user creation data
   */
  static validateCreateUser(options: CreateUserOptions): UserValidationResult {
    const errors: string[] = [];

    if (!options.email?.trim()) {
      errors.push('Email is required');
    } else if (!UserManagementUtils.validateEmail(options.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!options.companyId?.trim()) {
      errors.push('Company ID is required');
    }


    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validates user update data
   */
  static validateUpdateUser(options: UpdateUserOptions): UserValidationResult {
    const errors: string[] = [];

    if (!options.userId?.trim()) {
      errors.push('User ID is required');
    }

    if (!options.companyId?.trim()) {
      errors.push('Company ID is required');
    }


    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Checks if a user already exists in the users list
   */
  static checkUserExists(email: string, users: ExtendedUser[]): boolean {
    return users.some(user => 
      user.email.toLowerCase() === email.toLowerCase()
    );
  }

  /**
   * Validates user permissions for management operations
   */
  static validateUserPermissions(
    currentUser: UserProfile | null,
    targetUserId: string,
    operation: 'update' | 'remove',
    companyId: string
  ): UserValidationResult {
    const errors: string[] = [];

    if (!currentUser) {
      errors.push('Authentication required');
      return { isValid: false, errors };
    }

    // For now, we'll implement basic validation
    // TODO: Implement proper permission checking with company ownership validation
    if (currentUser.id === targetUserId && operation === 'remove') {
      errors.push('You cannot remove yourself from the company');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Creates/invites a new user to a company
   * Uses the existing invite-user API endpoint
   * 
   * The backend automatically:
   * - Creates/invites the user in Supabase Auth
   * - Creates a profile with default settings (see getDefaultUserSettings())
   * - Adds the user to the company members
   * - Ensures existing users without profiles get profiles created
   */
  static async createUser(
    options: CreateUserOptions,
    existingUsers: ExtendedUser[] = []
  ): Promise<UserManagementOperationResult> {
    try {
      // Validate input
      const validation = UserManagementUtils.validateCreateUser(options);
      if (!validation.isValid) {
        return {
          success: false,
          message: validation.errors.join(', '),
          error: 'Validation failed'
        };
      }

      // Check if user already exists
      if (UserManagementUtils.checkUserExists(options.email, existingUsers)) {
        return {
          success: false,
          message: 'This user is already a member of the company',
          error: 'User already exists'
        };
      }

      // Prepare request for the API
      const inviteRequest: InviteUserRequest = {
        email: options.email.trim(),
        settings: options.settings,
        role: options.role,
        companyId: options.companyId,
        gameIds: options.gameIds
      };

      // Call the API service
      const result = await locanestApiService.inviteUser(inviteRequest);
      console.log('Invite user result:', result);
      return {
        success: result.success,
        message: result.message,
        error: result.error,
        data: result.data
      };
    } catch (error) {
      console.error('Error in createUser:', error);
      return {
        success: false,
        message: 'Failed to invite user. Please try again.',
        error: 'Network error'
      };
    }
  }

  /**
   * Updates a user's role and game access
   * Uses the new update-user API endpoint
   */
  static async updateUser(options: UpdateUserOptions): Promise<UserManagementOperationResult> {
    try {
      // Validate input
      const validation = UserManagementUtils.validateUpdateUser(options);
      if (!validation.isValid) {
        return {
          success: false,
          message: validation.errors.join(', '),
          error: 'Validation failed'
        };
      }

      // Call the API service
      const result = await apiService.updateUser({
        userId: options.userId,
        companyId: options.companyId,
        role: options.role as any, // Type cast for custom roles support
        gameAccess: options.gameAccess
      });
      
      return {
        success: result.success,
        message: result.success ? 'User updated successfully' : result.error || 'Failed to update user',
        error: result.error,
        data: result.data
      };
    } catch (error) {
      console.error('Error in updateUser:', error);
      return {
        success: false,
        message: 'Failed to update user. Please try again.',
        error: 'Network error'
      };
    }
  }

  /**
   * Removes a user from a company and all its games
   * Uses the new remove-user API endpoint
   */
  static async removeUser(
    userId: string, 
    companyId: string,
    currentUser: UserProfile | null
  ): Promise<UserManagementOperationResult> {
    try {
      // Validate permissions
      const permissionValidation = UserManagementUtils.validateUserPermissions(
        currentUser, 
        userId, 
        'remove', 
        companyId
      );

      if (!permissionValidation.isValid) {
        return {
          success: false,
          message: permissionValidation.errors.join(', '),
          error: 'Permission denied'
        };
      }

      // Call the API service
      const result = await apiService.removeUser({
        userId,
        companyId
      });
      
      return {
        success: result.success,
        message: result.success ? 
          `User removed from company and ${result.data?.removedFrom?.games || 0} games successfully` : 
          result.error || 'Failed to remove user',
        error: result.error,
        data: result.data
      };
    } catch (error) {
      console.error('Error in removeUser:', error);
      return {
        success: false,
        message: 'Failed to remove user. Please try again.',
        error: 'Network error'
      };
    }
  }

  /**
   * Formats last login date to human-readable format
   */
  static formatLastLogin(dateString?: string): string {
    if (!dateString) return "Never";
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  }

  /**
   * Transforms user data to ExtendedUser format with game access
   */
  static transformUserToExtended(
    user: UserProfile,
    companyId: string,
    games: Array<{ id: string; name: string; member_ids: string[] }>,
    userRole: string = "contributor"
  ): ExtendedUser {
    return {
      ...user,
      role: userRole,
      company_id: companyId,
      gameAccess: games.map(game => ({
        gameId: game.id,
        gameName: game.name,
        hasAccess: game.member_ids.includes(user.id)
      })),
      lastLogin: user.updated_at
    };
  }

  /**
   * Handles role change logic including automatic game access for owners
   */
  static updateUserRole(
    users: ExtendedUser[],
    userId: string,
    newRole: string
  ): ExtendedUser[] {
    return users.map(user => {
      if (user.id === userId) {
        const updatedUser = { ...user, role: newRole };

        // If changing to owner, grant access to all games
        if (newRole === "owner") {
          updatedUser.gameAccess = updatedUser.gameAccess.map(access => ({
            ...access,
            hasAccess: true
          }));
        }

        return updatedUser;
      }
      return user;
    });
  }

  /**
   * Handles game access change for a specific user
   */
  static updateUserGameAccess(
    users: ExtendedUser[],
    userId: string,
    gameId: string,
    hasAccess: boolean
  ): { users: ExtendedUser[]; error?: string } {
    let error: string | undefined;

    const updatedUsers = users.map(user => {
      if (user.id === userId) {
        // Owners can't have their game access individually modified
        if (user.role.toLowerCase() === "owner") {
          error = "Owners have access to all games by default";
          return user;
        }

        return {
          ...user,
          gameAccess: user.gameAccess.map(access =>
            access.gameId === gameId ? { ...access, hasAccess } : access
          )
        };
      }
      return user;
    });

    return { users: updatedUsers, error };
  }

  /**
   * Calculates the changes between original and current user states
   */
  static calculateUserChanges(originalUsers: ExtendedUser[], currentUsers: ExtendedUser[]) {
    return {
      removedUsers: originalUsers.filter(
        orig => !currentUsers.find(u => u.id === orig.id)
      ),
      newUsers: currentUsers.filter(u => u.id.startsWith("temp-")),
      roleChanges: currentUsers.filter(u => {
        const orig = originalUsers.find(o => o.id === u.id);
        return orig && orig.role !== u.role;
      }),
      accessChanges: currentUsers.filter(u => {
        const orig = originalUsers.find(o => o.id === u.id);
        return (
          orig &&
          JSON.stringify(orig.gameAccess) !== JSON.stringify(u.gameAccess)
        );
      })
    };
  }

  /**
   * Validates if a user can be removed (not an owner, not current user)
   */
  static canRemoveUser(
    user: ExtendedUser,
    currentUserId?: string
  ): { canRemove: boolean; reason?: string } {
    if (user.role.toLowerCase() === "owner") {
      return {
        canRemove: false,
        reason: "Company owners cannot be removed"
      };
    }

    if (user.id === currentUserId) {
      return {
        canRemove: false,
        reason: "You cannot remove yourself"
      };
    }

    return { canRemove: true };
  }
}

// Export individual functions for backward compatibility and convenience
export const {
  getDefaultUserSettings,
  validateEmail,
  validateCreateUser,
  validateUpdateUser,
  checkUserExists,
  validateUserPermissions,
  createUser,
  updateUser,
  removeUser,
  formatLastLogin,
  transformUserToExtended,
  updateUserRole,
  updateUserGameAccess,
  calculateUserChanges,
  canRemoveUser
} = UserManagementUtils;

// Default export
export default UserManagementUtils;
